import CheckIcon from '@mui/icons-material/CheckRounded';
import { Typography } from '@mui/material';

import { cn } from '@/shared/lib/cn';

import type { ChapterState, FlowChapter } from './grouping';

interface FlowChapterBubbleProps {
  chapter: FlowChapter;
  state: ChapterState;
  /** Position in the whole queue, for marking this chapter's current pip. */
  index: number;
  outcomes: Map<string, 'done' | 'skipped'>;
}

/** Tint and ink per state, kept in one place so the four never drift apart. */
const BUBBLE_CLASSES: Record<ChapterState, string> = {
  active: 'bg-brand-50 inset-ring-brand-200',
  sealed: 'bg-success/8 inset-ring-success/20',
  partial: 'bg-warning/8 inset-ring-warning/20',
  pending: 'bg-transparent inset-ring-transparent',
};

const NAME_CLASSES: Record<ChapterState, string> = {
  active: 'text-primary-dark',
  sealed: 'text-success-dark',
  partial: 'text-warning-dark',
  pending: 'text-content-disabled',
};

/**
 * One company in the flow's rail, holding its own documents as pips.
 *
 * A sealed bubble is the payoff: the company's last document resolved with
 * nothing skipped, so it takes a filled tick and green ink and stays that way
 * for the rest of the run. `partial` is the deliberately duller sibling — the
 * company closed with something skipped, so it is marked as visited but never
 * ticked.
 */
export const FlowChapterBubble = ({
  chapter,
  state,
  index,
  outcomes,
}: FlowChapterBubbleProps) => {
  const isClosed = state === 'sealed' || state === 'partial';

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1.5 rounded-lg px-2 py-1.5 inset-ring',
        'transition-[background-color,box-shadow] duration-300 ease-out',
        'motion-reduce:transition-none',
        BUBBLE_CLASSES[state],
      )}
    >
      <div className="flex items-center gap-1">
        {isClosed && (
          <span
            className={cn(
              'flex size-3.5 shrink-0 items-center justify-center rounded-full [&>svg]:text-[10px]',
              state === 'sealed' ? 'bg-success text-white' : 'bg-warning/25 text-warning-dark',
            )}
          >
            {state === 'sealed' ? <CheckIcon fontSize="inherit" /> : null}
          </span>
        )}

        <Typography
          variant="caption"
          component="span"
          className={cn(
            'block max-w-36 truncate text-[11px] leading-tight font-medium',
            NAME_CLASSES[state],
          )}
        >
          {chapter.companyName}
        </Typography>
      </div>

      {/* One pip per document-step, so a company's own workload is legible
          inside its bubble rather than averaged into a single bar. */}
      <div className="flex items-center gap-1">
        {chapter.steps.map((step, stepIndex) => {
          const outcome = outcomes.get(step.id);
          const isCurrent = chapter.startIndex + stepIndex === index;

          return (
            <span
              key={step.id}
              className={cn(
                'h-1 w-4 rounded-full transition-colors duration-300 ease-out',
                'motion-reduce:transition-none',
                outcome === 'done' && 'bg-success',
                outcome === 'skipped' && 'bg-warning/45',
                outcome === undefined &&
                  (isCurrent ? 'bg-primary-dark' : 'bg-content-disabled/30'),
              )}
            />
          );
        })}
      </div>
    </div>
  );
};
